import React, { useState, useEffect } from 'react';
import { getRelationships } from '../services/api';
// import { ChevronLeft, Search, GitBranch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function RelationshipsPage() {
    const [relationships, setRelationships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedTable, setSelectedTable] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        loadAllRelationships();
    }, []);

    const loadAllRelationships = async () => {
        try {
            // Use the API service which handles auth and base URL
            const res = await getRelationships();
            setRelationships(res.data.relationships || []);
        } catch (err) {
            console.error('Error loading relationships:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredRelationships = relationships.filter(rel => {
        const matchesSearch = search ? 
            rel.from_table.toLowerCase().includes(search.toLowerCase()) ||
            rel.to_table.toLowerCase().includes(search.toLowerCase()) : true;
        const matchesTable = selectedTable ? 
            rel.from_table === selectedTable || rel.to_table === selectedTable : true;
        return matchesSearch && matchesTable;
    });

    // Get unique tables for filter dropdown
    const uniqueTables = [...new Set([
        ...relationships.map(r => r.from_table),
        ...relationships.map(r => r.to_table)
    ])].sort();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-4">
                        <div className="flex items-center">
                            <button
                                onClick={() => navigate(-1)}
                                className="mr-4 p-2 hover:bg-gray-100 rounded"
                            >
                                <span>←</span>
                            </button>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Entity Relationships
                            </h1>
                        </div>
                        <div className="text-sm text-gray-500">
                            {filteredRelationships.length} relationships
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Filters */}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
                        <input
                            type="text"
                            placeholder="Search tables..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={selectedTable}
                        onChange={(e) => setSelectedTable(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">All Tables</option>
                        {uniqueTables.map(table => (
                            <option key={table} value={table}>{table}</option>
                        ))}
                    </select>
                </div>

                {/* Relationships List */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    From Table
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    From Column
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <span className="mx-auto">🔀</span>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    To Table
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    To Column
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredRelationships.map((rel, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {rel.from_table}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {rel.from_column}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <span className="text-gray-400">→</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {rel.to_table}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {rel.to_column}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}